import React from 'react';
import { useParams } from 'react-router-dom';
import {
  Chat,
  ChatMessageSendEvent,
  ChatMessageTemplateProps
} from '@progress/kendo-react-conversational-ui';

import {
  PlayerChatContext,
  PlayerChatContextProps
} from '../../../contexts/player-chat';
import MessageChat from '../message-chat';
import { MessageTemplate } from '../message-template';
import { PlayerContext } from '../../../contexts/player';
import { ChatState } from '../../../types/state/websocket/chat-state';
import { GameDocumentContext } from '../../../contexts/game-document';
import { DisplayLanguageContext } from '../../../contexts/display-languages';
import { GetPlayerFacilitatorChatHistoryAsync } from '../../../services/players';
import { generateTitleById } from '../../../utils/game-document/display-languages';

interface ChatRoom {
  visibleButtonChat: boolean;
  addNewMessage: (event: ChatMessageSendEvent) => Promise<void>;
}

export const FacilitatorChat = ({
  visibleButtonChat,
  addNewMessage
}: ChatRoom) => {
  const { gameCode } = useParams();

  const [playerState] = React.useContext(PlayerContext);
  const [gameDocumentstate] = React.useContext(GameDocumentContext);
  const [displayLanguageContext] = React.useContext(DisplayLanguageContext);

  const { chatHub } = React.useContext(
    PlayerChatContext
  ) as PlayerChatContextProps;

  const [messages, setMessages] = React.useState<ChatState[]>([]);

  const playerCode = playerState.playerState?.code ?? '';
  const user = {
    id: playerCode,
    avatarUrl: playerState?.playerState?.avatarImage
  };

  const renderMessageTemplate = React.useCallback(
    (props: ChatMessageTemplateProps) => (
      <MessageTemplate
        hideChatButton={true}
        currentUserCode={user.id!}
        {...props}
      />
    ),
    [user.id]
  );

  const getChatHistoryAsync = async () => {
    let newChats: ChatState[] = [];
    GetPlayerFacilitatorChatHistoryAsync(gameCode!, playerCode).then(
      (response) => {
        response?.forEach((chat) => {
          newChats.push({
            author: {
              id: chat.playerCode,
              name: chat.playerName ?? '',
              avatarUrl: chat.playerAvatar ?? ''
            },
            timestamp: new Date(chat.sentDateUtc),
            group: 'facilitator-chat',
            text: chat.message
          });
        });

        setMessages(newChats);
      }
    );
  };

  React.useEffect(() => {
    if (!chatHub || !gameCode) return;

    chatHub.on('FacilitatorChatReceived', (data) => {
      const { senderCode, senderName, senderAvatar, message, sentDateUtc } =
        data;
      const chat = {
        author: {
          id: senderCode,
          name: senderName,
          avatarUrl: senderAvatar
        },
        timestamp: new Date(sentDateUtc),
        group: 'facilitator-chat',
        text: message
      };
      setMessages((prev) => [...prev, chat]);
    });
  }, [chatHub, gameCode]);

  React.useEffect(() => {
    getChatHistoryAsync();
  }, []);

  return (
    <div className="h-100 w-100 row m-0 p-0 flex-grow-1 overflow-hidden">
      <div className={`col-12 col-md-12 m-0 p-0`}>
        <div className="d-flex h-100 facilitator-chat-container">
          <Chat
            className={`${!visibleButtonChat ? 'hide-action' : ''} flex-grow-1 w-100 h-100`}
            user={user}
            messages={messages}
            onMessageSend={(e) => {
              addNewMessage(e);

              const newMessage = {
                author: {
                  id: e.message.author.id,
                  name: e.message.author.name,
                  avatarUrl: e.message.author.avatarUrl
                },
                timestamp: e.message.timestamp,
                group: 'player-chat',
                text: e.message.text
              };
              setMessages((prev) => [...prev, newMessage]);
            }}
            placeholder={
              generateTitleById(
                '2373ccd3-e73e-403d-bd99-7083f1512a96',
                gameDocumentstate,
                displayLanguageContext.displayLanguageSelected.resources!,
                'game'
              ) || 'Type a message...'
            }
            message={MessageChat}
            messageTemplate={renderMessageTemplate}
          />
        </div>
      </div>
    </div>
  );
};
